/**
 * IdentifierForm.tsx
 *
 * Reusable "email or phone" identifier collection form.
 * Used by the login-id screen to collect the initial identifier.
 *
 * PROPS
 * -----
 * onSubmit(identifier: string)  — called with the trimmed value on "Continue"
 * defaultValue                  — pre-fills the field (e.g., on back-navigation)
 * isLoading                     — disables the button while Auth0 is processing
 * errorMessage                  — displays an Auth0 error banner (no-account-found, etc.)
 * signupHref                    — link href for "Don't have an account?" (optional)
 *
 * VALIDATION
 * ----------
 * Client-side only: checks for a valid email format OR a phone pattern.
 * Auth0 performs definitive server-side validation.
 *
 * Auth0 / ACUL note
 * -----------------
 * On submit, the parent screen calls `loginId.login({ username })`.
 * Auth0 then decides which next screen to show based on tenant config.
 */

import { useId, useRef, useState } from "react";

import { CustomInput } from "./CustomInput";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IdentifierFormProps {
  onSubmit: (identifier: string) => void;
  defaultValue?: string;
  isLoading?: boolean;
  errorMessage?: string;
  signupHref?: string;
}

// ---------------------------------------------------------------------------
// Validation helper (client-side only — see src/utils/identifier.ts)
// ---------------------------------------------------------------------------

function isValidIdentifier(value: string): boolean {
  const trimmed = value.trim();
  // Email: basic RFC 5322 subset
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // North-American phone: 10 digits, optional +1, allow spaces/dashes/dots/parens
  const phoneRe = /^(\+?1[\s.-]?)?(\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}$/;
  return emailRe.test(trimmed) || phoneRe.test(trimmed);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IdentifierForm({
  onSubmit,
  defaultValue = "",
  isLoading = false,
  errorMessage,
  signupHref,
}: IdentifierFormProps) {
  const [value, setValue] = useState(defaultValue);
  const [touched, setTouched] = useState(false);
  const uid = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const clientError =
    touched && value.trim() && !isValidIdentifier(value)
      ? "Enter a valid email address or mobile number."
      : undefined;

  const canSubmit = !isLoading && value.trim().length > 0 && !clientError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!isValidIdentifier(value)) {
      inputRef.current?.focus();
      return;
    }
    onSubmit(value.trim());
  };

  return (
    <form className="auth-view" onSubmit={handleSubmit} noValidate>
      {/* Auth0 server error banner */}
      {errorMessage && (
        <div className="auth-error-banner" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="auth-fields">
        <CustomInput
          ref={inputRef}
          id={`${uid}-identifier`}
          label="Email address or mobile number"
          type="text"
          autoComplete="username"
          placeholder="you@example.com or (555) 555-5555"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setTouched(true)}
          error={clientError}
          required
        />
      </div>

      <div className="auth-submit-block">
        <button type="submit" className="auth-btn" disabled={!canSubmit}>
          {isLoading ? "Please wait…" : "Continue"}
        </button>

        {signupHref !== undefined && (
          <p className="auth-nav-link">
            Don&rsquo;t have an account?{" "}
            <a href={signupHref || "#"}>Sign up here</a>
          </p>
        )}
      </div>
    </form>
  );
}

export default IdentifierForm;
